import * as React from "react";

import Progress from "../common/organism/progress";
import { Contract } from "../../domain/model/contract";
import { Project } from "../../domain/model/project";
import PageWrapper from "../common/atom/wrapper/page_wrapper";
import ProjectThumbnail from "../component/client/contract/share/project_thumbnail";
import { TBreadcrumbItem } from "../type/common";
import { routeConfig } from "../../config/language/ja-JP/presentation/route_text";
import Breadcrumbs from "../common/molecular/breadcrumbs/breadcrumbs";
import SubmitButtonWrapper from "../common/atom/wrapper/submit_btn_wrapper/submit_btn_wrapper";
import BtnCorporateLarge from "../common/atom/btn/btn_corporate_large";
import { entity } from "../../infra/api/rpc/api";
import Table from "../common/atom/table/table";
import Tbody from "../common/atom/table/tbody";
import Tr from "../common/atom/table/tr";
import ThWithText from "../common/atom/table/th_with_text";
import TdWithText from "../common/atom/table/td_with_text";
import styled from "styled-components";
import {
  ConstColor,
  ConstFontWeight,
} from "../../config/styles_const/styles_const";
import { RouteComponentProps } from "react-router";
import { createApiClient, IApiClient } from "../../infra/api/client";
import { createProjectAPI } from "../../infra/api/service/project_api";
import { createContractAPI } from "../../infra/api/service/contract_api";
import { IProjectRepository } from "../../domain/repository/project_repository";
import { IContractRepository } from "../../domain/repository/contract_repository";

const apiClient: IApiClient = createApiClient();
const projectRepository: IProjectRepository = createProjectAPI(apiClient);
const contractRepository: IContractRepository = createContractAPI(apiClient);

interface IProps
  extends RouteComponentProps<{
    companyId: string;
    projectId: string;
    customerId: string;
  }> {}

const CustomerContract: React.FC<IProps> = (props) => {
  const [project, setProject] = React.useState<Project | null>(null);
  const [contract, setContract] = React.useState<Contract | null>(null);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const companyId = props.match.params.companyId;
  const projectId = props.match.params.projectId;
  const customerId = props.match.params.customerId;

  React.useEffect(() => {
    contractRepository.get(companyId, projectId, customerId).then((c) => {
      setContract(c);
    });

    projectRepository.get(projectId).then((p) => {
      setProject(p);
    });
  }, []);

  // 初期化
  if (contract == null || project == null) {
    return <Progress canAnimation={false} />;
  }

  const breadcrumbItems: TBreadcrumbItem[] = [
    routeConfig.clientRoot,
    { text: `[成約報告]${project!.name}` },
  ];

  const createdAtString: string = contract.getCreatedAtString();
  const projectName: string = project.name;
  const fileUrl: string = contract.gsFileURL;

  return (
    <React.Fragment>
      {isLoading && <Progress canAnimation={false} />}
      <PageWrapper>
        <Breadcrumbs breadcrumbItems={breadcrumbItems} />
        <ProjectThumbnail project={project} />

        <TableWrapper>
          <Table>
            <Tbody>
              <Tr>
                <ThWithText>成約ステータス</ThWithText>
                <TdWithText colSpan={3}>{contract.statusStr()}</TdWithText>
              </Tr>
              <Tr>
                <ThWithText>成約報告日</ThWithText>
                <TdWithText colSpan={3}>{createdAtString}</TdWithText>
              </Tr>
              <Tr>
                <ThWithText>案件名</ThWithText>
                <TdWithText colSpan={3}>{projectName}</TdWithText>
              </Tr>
              <Tr>
                <ThWithText>契約書</ThWithText>
                <TdWithText colSpan={3}>
                  {" "}
                  {fileUrl && (
                    <FileLinkText
                      href={fileUrl}
                      // onClick={this.handleClickFile}
                      target="_blank"
                      rel="noopener"
                    >
                      File
                    </FileLinkText>
                  )}
                </TdWithText>
              </Tr>
            </Tbody>
          </Table>
        </TableWrapper>

        <TableWrapper>
          <Table>
            <Tbody>
              <Tr>
                <ThWithText>契約のタイトル</ThWithText>
                <TdWithText colSpan={3}>{contract.title}</TdWithText>
              </Tr>
              <Tr>
                <ThWithText>契約時期</ThWithText>
                <TdWithText colSpan={3}>
                  {contract.startFromYM}〜{contract.endToYM}
                </TdWithText>
              </Tr>
              {contract.hasMeetingInfo() && (
                <Tr>
                  <ThWithText>打ち合わせ</ThWithText>
                  <TdWithText colSpan={3}>
                    {contract.meetingNum}/{contract.meetingIntervalStr()}
                    の頻度で、1回あたり約{contract.meetingMinutes}
                    分の打ち合わせを想定
                  </TdWithText>
                </Tr>
              )}
              {contract.hasWorkingInfo() && (
                <Tr>
                  <ThWithText>実稼働</ThWithText>
                  <TdWithText colSpan={3}>
                    約{contract.workingHour}時間/{contract.workingIntervalStr()}
                    の実稼働を想定
                  </TdWithText>
                </Tr>
              )}
              <Tr>
                <ThWithText>契約の報酬</ThWithText>
                <TdWithText colSpan={3}>{contract.feePerMonth}円/月</TdWithText>
              </Tr>
              <Tr>
                <ThWithText>契約の種類</ThWithText>
                <TdWithText colSpan={3}>
                  {contract.contractTypeStr()}
                </TdWithText>
              </Tr>
              <Tr>
                <ThWithText>業務内容</ThWithText>
                <TdWithText colSpan={3}>{contract.description}</TdWithText>
              </Tr>
            </Tbody>
          </Table>
        </TableWrapper>

        {!contract!.isApproved &&
          contract!.requester ===
            entity.Contract.Requester.Requester_Company && (
            <SubmitButtonWrapper
              onClick={async () => {
                setIsLoading(true);
                await contractRepository.approve(projectId, customerId);
                const c = await contractRepository.get(
                  companyId,
                  projectId,
                  customerId
                );
                setContract(c);
                setIsLoading(false);
              }}
            >
              <BtnCorporateLarge type="submit" widthSize="200px">
                承認する
              </BtnCorporateLarge>
            </SubmitButtonWrapper>
          )}
      </PageWrapper>
    </React.Fragment>
  );
};

export default CustomerContract;

const TableWrapper = styled.div`
  margin-bottom: 40px;
`;

const FileLinkText = styled.a`
    font-weight: ${ConstFontWeight.BOLD}
    color: ${ConstColor.Corporate};
`;



// WEBPACK FOOTER //
// ./src/presentation/container/customer_contract_container.tsx